import { useLoad } from "@redotech/react-util/load";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { BaseTeaser } from "../base-teaser";
import { FixedContainer } from "./fixed-container";
import { SharedFormModal } from "./shared-form-modal";
import { SignupFormLocalData, SignupFormProps } from "./types";
import {
  EXPIRATION_PERIOD,
  generateAnonymousCustomerId,
  getNextStepIndex,
  isOnMobile,
  isValidURLtoShowForm,
  submitFormStep,
  useLocalStorage,
  useSignupFormAnalytics,
} from "./utils";

export const RedoMarketingPopupFormInstance = memo(
  function RedoMarketingPopupFormInstance(props: SignupFormProps) {
    const mode = props.mode ?? "normal";
    const localStorageKey =
      mode === "exit-intent"
        ? "redo.marketing-exit-intent-form"
        : "redo.marketing-signup-form";

    const [open, setOpen] = useState(false);
    const [hasEverBeenOpened, setHasEverBeenOpened] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [discountCode, setDiscountCode] = useState<string | undefined>();
    const [localData, setLocalData] = useLocalStorage<SignupFormLocalData>(
      localStorageKey,
      {},
    );

    useEffect(() => {
      if (!localData.customerHash) {
        const customerHash =
          props.shopifyCustomerId ?? generateAnonymousCustomerId();
        setLocalData((prev) => ({ ...prev, customerHash }));
      }
    }, [localData.customerHash, props.shopifyCustomerId, setLocalData]);

    const forceOpen = props.forceId || props.forceForm;
    const client = props.rpcClient;

    const formLoad = useLoad(
      async (signal) => {
        if (props.forceForm) {
          return { form: props.forceForm, formId: null, skipPhoneSteps: false };
        }
        if (!client || !localData.customerHash) return undefined;

        const result = await client.getSignupForm(
          {
            customerHash: localData.customerHash.toString(),
            shopifyCustomerId: props.shopifyCustomerId ?? null,
            redoCustomerId: props.customerId ?? null,
            formId:
              mode === "exit-intent"
                ? null
                : localData.formId?.toString() || null,
            isOnMobile: isOnMobile(),
            site: props.site,
            utmParams: props.utmParams ?? "",
            forceId: mode === "exit-intent" ? undefined : props.forceId,
            exitIntentOnly: mode === "exit-intent" ? true : undefined,
          },
          { signal },
        );

        if (result.formId && !props.forceId && mode === "normal") {
          setLocalData((prev: SignupFormLocalData) => ({
            ...prev,
            formId: result.formId ?? undefined,
          }));
        }

        return result;
      },
      [
        client,
        localData.customerHash,
        props.shopifyCustomerId,
        props.customerId,
        props.utmParams,
        mode,
      ],
    );

    const form = formLoad.value?.form ?? null;
    const formId = formLoad.value?.formId ?? null;
    const treatmentId = formLoad.value?.treatmentId ?? null;
    const isValidURL = isValidURLtoShowForm(form);
    const isCorrectSite = props.site === form?.behavior.appearsOnSite;
    const shouldDisplay = !!(form && isCorrectSite && isValidURL);
    const { logAnalytics } = useSignupFormAnalytics({
      client,
      formId,
      treatmentId,
      shopifyCustomerId: props.shopifyCustomerId,
      localData,
      setLocalData,
    });

    const openModal = useCallback(async () => {
      if (open) return;
      setOpen(true);
      setHasEverBeenOpened(true);
      setCurrentStep(
        getNextStepIndex(form, formLoad.value?.skipPhoneSteps ?? true, 0),
      );

      if (
        !localData.formViewedAt &&
        localData.customerHash &&
        client &&
        formId
      ) {
        await logAnalytics("view");
      }
    }, [
      open,
      localData,
      client,
      formId,
      logAnalytics,
      setOpen,
      setHasEverBeenOpened,
      setCurrentStep,
      form,
      formLoad.value?.skipPhoneSteps,
    ]);

    // Time-on-page auto-open for normal mode
    useEffect(() => {
      if (mode !== "normal") return;
      if (
        completed ||
        !form ||
        !formId ||
        !form.behavior.display.timeOnPage.enabled ||
        !isValidURL
      )
        return;

      const previousAutoOpenExpired =
        localData.formAutoOpenedAt &&
        Date.now() - localData.formAutoOpenedAt.getTime() > EXPIRATION_PERIOD;

      if (localData.formAutoOpenedAt && !previousAutoOpenExpired && !forceOpen)
        return;

      const timeout = setTimeout(() => {
        if (localData.formAutoOpenedAt && !forceOpen) return;
        setLocalData((prev: SignupFormLocalData) => ({
          ...prev,
          formAutoOpenedAt: new Date(),
        }));
        openModal().catch((e) =>
          console.error("Failed to auto-open signup form", e),
        );
      }, form.behavior.display.timeOnPage.seconds * 1000);

      return () => clearTimeout(timeout);
    }, [
      mode,
      form,
      formId,
      completed,
      localData,
      setLocalData,
      openModal,
      forceOpen,
      isValidURL,
    ]);

    // Exit intent detection for exit-intent mode
    useEffect(() => {
      if (mode !== "exit-intent") return;
      if (
        completed ||
        !form ||
        !formId ||
        !form.behavior.display.exitIntent?.enabled ||
        !isValidURL ||
        open
      )
        return;

      const previousTriggerExpired =
        localData.lastTriggeredAt &&
        Date.now() - localData.lastTriggeredAt.getTime() > EXPIRATION_PERIOD;

      if (localData.lastTriggeredAt && !previousTriggerExpired) return;

      let hasTriggered = false;
      let lastY = 0;
      let lastTime = Date.now();

      const handleMouseMove = (e: MouseEvent) => {
        const currentTime = Date.now();
        const timeDiff = currentTime - lastTime;
        const yDiff = e.clientY - lastY;

        const velocity = timeDiff > 0 ? yDiff / timeDiff : 0;

        lastY = e.clientY;
        lastTime = currentTime;

        if (e.clientY <= 50 && velocity < -0.5 && !hasTriggered) {
          hasTriggered = true;
          setLocalData((prev: SignupFormLocalData) => ({
            ...prev,
            lastTriggeredAt: new Date(),
          }));
          openModal().catch((e) =>
            console.error("Failed to open exit intent form", e),
          );
        }
      };

      const handleMouseLeave = (e: MouseEvent) => {
        if (e.clientY <= 20 && !hasTriggered) {
          hasTriggered = true;
          setLocalData((prev: SignupFormLocalData) => ({
            ...prev,
            lastTriggeredAt: new Date(),
          }));
          openModal().catch((e) =>
            console.error("Failed to open exit intent form", e),
          );
        }
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseleave", handleMouseLeave);
      };
    }, [
      mode,
      form,
      formId,
      completed,
      localData,
      setLocalData,
      openModal,
      isValidURL,
      open,
    ]);

    const showTeaser = useMemo(() => {
      if (mode === "exit-intent") return false;
      if (forceOpen) return true;
      if (!form?.teaser?.enabled || !isCorrectSite) return false;
      if (localData.atLeastOneStepSubmittedAt) return false;
      if (form.teaser.visibility === "after-closing") {
        return hasEverBeenOpened && !completed;
      }
      if (!isValidURL) {
        return false;
      }

      return true;
    }, [
      mode,
      forceOpen,
      form?.teaser,
      isCorrectSite,
      localData.atLeastOneStepSubmittedAt,
      hasEverBeenOpened,
      completed,
      isValidURL,
    ]);

    useEffect(() => {
      props.onShouldDisplayChanged?.(shouldDisplay);
    }, [shouldDisplay, props]);

    if (!form) return null;

    return (
      <>
        {form.teaser && showTeaser && (
          <FixedContainer teaser={form.teaser}>
            <BaseTeaser onClick={openModal} teaser={form.teaser} />
          </FixedContainer>
        )}
        <SharedFormModal
          ariaLabel="Marketing signup form"
          brandKit={props.brandKit}
          client={client}
          completed={completed}
          currentStep={currentStep}
          discountCode={discountCode}
          form={form}
          merchantPhoneNumber={formLoad.value?.merchantPhoneNumber}
          onClose={() => setOpen(false)}
          onSubmitStep={async (i: number, formData: Record<string, string>) => {
            const entryPoint = props.entryPoint;
            const result = await submitFormStep({
              i,
              formData,
              form,
              formId,
              treatmentId,
              client,
              customerId: props.customerId,
              localData,
              logAnalytics,
              setCurrentStep,
              entryPoint,
              skipPhoneSteps: formLoad.value?.skipPhoneSteps ?? true,
              onLastStepSubmitted: () => {
                setOpen(false);
                setCompleted(true);
              },
            });
            if (result.discountCode) {
              setDiscountCode(result.discountCode);
            }
          }}
          open={open}
        />
      </>
    );
  },
);
